import { useViewManager, useScanReviewAppContext } from './ScanReview.hooks';
import { ScanReviewMarginMarkingToolController } from './ScanReviewMarginMarkingToolController';
import {
    ScanReviewMarginLineEditor,
    ScanReviewMarginMarkingToolLiveObjectsProvider,
} from './ScanReviewMarginMarkingToolServices';
import type { ScanReviewEditedMarginLine } from './ScanReviewMarginMarkingToolTypes';
import { ScanReviewPartialScene } from './ScanReviewSceneTypes';
import { ensureMeshIndex } from '@orthly/forceps';
import { type ToothNumber, ToothUtils } from '@orthly/items';
import constate from 'constate';
import React from 'react';

interface ScanReviewMarginMarkingViewProps {
    marginLines: Map<ToothNumber, ScanReviewEditedMarginLine>;
    toothNumber?: ToothNumber;
    onMarginUpdate?: (editedMarginLine: ScanReviewEditedMarginLine) => void;
}

function useScanReviewMarginMarkingViewApp({
    marginLines,
    toothNumber,
    onMarginUpdate,
}: ScanReviewMarginMarkingViewProps) {
    const { lowerJawFactory, upperJawFactory, displayType, jawDisplayState, viewState } = useScanReviewAppContext();
    const viewManager = useViewManager(viewState);

    //Note:
    // We use refs here otherwise switching display related state
    // will cause the partial scene to be reconstructed for no
    // reason.
    const displayTypeRef = React.useRef(displayType);
    const jawDisplayStateRef = React.useRef(jawDisplayState);
    const scene = React.useMemo(() => {
        const upperJaw = upperJawFactory?.() ?? null;
        const lowerJaw = lowerJawFactory?.() ?? null;
        const scene = new ScanReviewPartialScene(upperJaw, lowerJaw);

        scene.setDisplayType(displayTypeRef.current, null);
        scene.setUpperJawVisibility(jawDisplayStateRef.current.upper);
        scene.setLowerJawVisibility(jawDisplayStateRef.current.lower);
        return scene;
    }, [upperJawFactory, lowerJawFactory]);

    const [activeTooth, setActiveTooth] = React.useState<ToothNumber | undefined>(toothNumber);

    const geometry = React.useMemo(() => {
        const isLowerTooth = activeTooth && ToothUtils.toothIsLower(activeTooth);
        if (isLowerTooth) {
            return scene.lowerJaw?.scanMesh.geometry;
        } else {
            return scene.upperJaw?.scanMesh.geometry;
        }
    }, [activeTooth, scene.lowerJaw?.scanMesh.geometry, scene.upperJaw?.scanMesh.geometry]);

    const indexBVH = React.useMemo(() => {
        if (geometry) {
            return ensureMeshIndex(geometry);
        }
    }, [geometry]);

    const marginLineEditors = React.useMemo(() => {
        return new Map([...marginLines.values()].map(m => [m.toothNumber, new ScanReviewMarginLineEditor(m.clone())]));
    }, [marginLines]);

    const currentEditor = React.useMemo(() => {
        if (!activeTooth) {
            return;
        }
        const editor = marginLineEditors.get(activeTooth);
        return editor;
    }, [activeTooth, marginLineEditors]);

    const liveObjectsProvider = React.useMemo(
        () => indexBVH && currentEditor && new ScanReviewMarginMarkingToolLiveObjectsProvider(indexBVH, currentEditor),
        [currentEditor, indexBVH],
    );

    const toolController = React.useMemo(
        () =>
            ScanReviewMarginMarkingToolController.makeController(
                viewManager,
                currentEditor,
                activeTooth,
                liveObjectsProvider,
                onMarginUpdate,
            ),
        [activeTooth, currentEditor, liveObjectsProvider, viewManager, onMarginUpdate],
    );

    return {
        activeTooth,
        setActiveTooth,
        marginLineEditors,
        scene,
        viewManager,
        toolController,
    };
}

export const [ScanReviewMarginMarkingViewAppProvider, useScanReviewMarginMarkingViewContext] = constate(
    useScanReviewMarginMarkingViewApp,
);
